import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Renders the hover + selection rectangles. The hover label includes an
 * element-kind glyph (`button` / `text` / `image` / etc.) and the smart
 * label so the user knows what they're about to click BEFORE they click.
 *
 * The label flips below the element when there's no room above, and
 * always stays inside the viewport horizontally.
 */
import { Fragment } from "react";
import { useElementRect, useElementRects } from "../hooks/useElementRect.js";
import { smartLabel } from "../dom/smartLabel.js";
const ACCENT_HEX = {
    indigo: "rgb(99 102 241)",
    amber: "rgb(250 204 21)",
    emerald: "rgb(34 197 94)",
};
const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 2147483646,
    pointerEvents: "none",
};
const rectStyle = (r, variant, accent) => ({
    position: "absolute",
    left: `${r.left}px`,
    top: `${r.top}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
    pointerEvents: "none",
    borderRadius: "4px",
    outline: `2px solid ${accent}`,
    outlineOffset: "-2px",
    background: variant === "hover"
        ? `${accent.replace("rgb(", "rgba(").replace(")", " / 0.10)")}`
        : `${accent.replace("rgb(", "rgba(").replace(")", " / 0.18)")}`,
    transition: "none",
});
const TAG_GLYPH = {
    button: "⏵",
    a: "↗",
    input: "✎",
    textarea: "✎",
    select: "▾",
    img: "🖼",
    nav: "☰",
    aside: "▤",
    header: "▭",
    footer: "▭",
    ul: "≡",
    ol: "≡",
    li: "•",
    form: "▤",
    h1: "H1",
    h2: "H2",
    h3: "H3",
    h4: "H4",
    h5: "H5",
    h6: "H6",
};
const glyphFor = (el) => {
    const tag = el.tagName.toLowerCase();
    return TAG_GLYPH[tag] ?? "◇";
};
const baseLabelStyle = (accent) => ({
    position: "absolute",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "8px",
    background: accent,
    color: "white",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    letterSpacing: "0.01em",
    maxWidth: "min(60vw, 480px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    boxShadow: "0 6px 18px rgb(0 0 0 / 0.35)",
});
/**
 * Position the label above the rect normally; if too close to the
 * viewport top, flip it below. Clamp horizontally so it never escapes
 * the viewport edge.
 */
const labelPositionFor = (r) => {
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 1280;
    const fitsAbove = r.top > 32;
    if (fitsAbove) {
        return {
            bottom: "calc(100% + 6px)",
            left: 0,
            maxWidth: `${Math.max(200, viewportW - r.left - 16)}px`,
        };
    }
    return {
        top: "calc(100% + 6px)",
        left: 0,
        maxWidth: `${Math.max(200, viewportW - r.left - 16)}px`,
    };
};
const tagBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    padding: "0 4px",
    borderRadius: "5px",
    background: "rgb(255 255 255 / 0.18)",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.02em",
};
export const Highlighter = ({ state, accent = "indigo" }) => {
    const hoverRect = useElementRect(state.hovered);
    const selectedRects = useElementRects(state.selected);
    if (state.phase === "idle")
        return null;
    const hoverAccent = ACCENT_HEX[accent];
    return (_jsxs("div", { style: overlayStyle, "aria-hidden": true, children: [hoverRect &&
                state.hovered &&
                !state.selected.includes(state.hovered) && (_jsx("div", { style: rectStyle(hoverRect, "hover", hoverAccent), children: _jsxs("span", { style: {
                        ...baseLabelStyle(hoverAccent),
                        ...labelPositionFor(hoverRect),
                    }, children: [_jsx("span", { style: tagBadgeStyle, children: glyphFor(state.hovered) }), _jsx("span", { children: smartLabel(state.hovered) })] }) })), selectedRects.map((r, i) => {
                const el = state.selected[i];
                if (!el)
                    return null;
                const selectedAccent = ACCENT_HEX["emerald"];
                return (_jsx(Fragment, { children: _jsx("div", { style: rectStyle(r, "selected", selectedAccent), children: _jsxs("span", { style: {
                                ...baseLabelStyle(selectedAccent),
                                ...labelPositionFor(r),
                                color: "rgb(15 15 17)",
                            }, children: [_jsx("span", { style: tagBadgeStyle, children: i + 1 }), _jsx("span", { children: smartLabel(el) })] }) }) }, i));
            })] }));
};
//# sourceMappingURL=Highlighter.js.map